/* @flow */
import { combineReducers } from 'redux-immutable';
import { OrderedMap, fromJS } from 'immutable';
import { toLower } from 'trustar/utils';
import type { Action } from 'trustar/utils/redux-types.js';
import { map } from 'ramda';
import { IO } from 'trustar/fp';

import {
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  FETCH_USER,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILURE,
  SAVE,
  SAVE_SUCCESS,
  SAVE_FAILURE,
  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,
  DEACTIVATE,
  DEACTIVATE_SUCCESS,
  DEACTIVATE_FAILURE,
  RESET_PASSWORD,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILURE,
  SET_DISPLAY_OPTIONS,
  AUTOSUGGEST,
} from './action-types.js';

import type { RawUser } from './types.js';
import { User, Options } from './types.js';

// The cases for "userName" and "emailAddress" fields are the
// result of legacy change. HOWEVER, the cases for "roles" and
// "enclaves" are necessary.
export const fromJSON = (rawUser: RawUser) => {
  const parsed = fromJS(rawUser);
  const companyEnclaves = parsed.getIn(['company', 'enclaves']);
  const result = parsed
    .updateIn(['company', 'enclaves'], v => v? v.toMap().mapKeys((k1, v1) => v1.get('id')) : v)
    .update('enclaves', v => {
      if (v) {
        const enclaves = companyEnclaves
        .map(enc => {
          const value = enc.get('id');
          const foundEnclave = v.find(e => e.get('id') === value);
          if (!foundEnclave) {
            return enc.toMap()
            .set('read', false)
            .set('create', false)
            .set('update', false);
          }
          return foundEnclave;
        });
        return enclaves.toMap().mapKeys((k1, v1) => v1.get('id'));
      }
      return v;
    })
    .mapKeys((k, v) => {
      switch (k) {
      case 'userName':
        return 'username';
      case 'emailAddress':
        return 'email';
      default:
        return k;
      }
    });

  return User(result);
};

// TODO: duplicated elsewhere
export const listToOrderedMap = (list: Array<any>) => {
  return fromJS(list)
    .toOrderedMap()
    .mapKeys((k, v) => v.get('guid'));
};

type UsersState = OrderedMap<string, typeof User>;

export const users = (state: UsersState = OrderedMap(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_USER_SUCCESS:
    return state.set(payload.guid, User(fromJS(payload).toOrderedMap()));
  case FETCH_SUCCESS: {
    const userMap = listToOrderedMap(payload.map(fromJSON));
    const isFirstPage = action.meta && action.meta.startPage === 0;
    return isFirstPage ? userMap : state.merge(userMap); // Append (merge) if we're paginating
  }
  case DELETE_SUCCESS:
    return state.delete(payload);

  default:
    return state;
  }
};

export const loading = (state: boolean = false, action: Action<*>) => {
  switch (action.type) {
  case FETCH:
  case SAVE:
  case DELETE:
  case DEACTIVATE:
  case RESET_PASSWORD:
    return true;
  case FETCH_SUCCESS:
  case FETCH_USER_SUCCESS:
  case FETCH_USER_FAILURE:
  case FETCH_FAILURE:
  case SAVE_SUCCESS:
  case SAVE_FAILURE:
  case DELETE_SUCCESS:
  case DELETE_FAILURE:
  case DEACTIVATE_SUCCESS:
  case DEACTIVATE_FAILURE:
  case RESET_PASSWORD_SUCCESS:
  case RESET_PASSWORD_FAILURE:
    return false;
  default:
    return state;
  }
};

export const options = (state: typeof Options = Options(), action: Action<Object>) => {
  const { type, payload } = action;

  switch (type) {
  case SET_DISPLAY_OPTIONS:
    return state.merge(payload);

  // Set hasNext after a fetch
  case FETCH_SUCCESS:
    return action.meta ? state.set('hasNext', action.meta.hasNext) : state;

  default:
    return state;
  }
};

export const autosuggestText = (state: string = '', action: Action<string>) => {
  const { type, payload } = action;

  switch (type) {
  case AUTOSUGGEST:
    return payload;
  default:
    return state;
  }
};

export default combineReducers({
  users,
  options,
  loading,
  autosuggestText,
});



// WEBPACK FOOTER //
// ./src/app/modules/users/stores.js